import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { HelmetProvider } from "react-helmet-async";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard/index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PageSpeed from "./pages/PageSpeed";
import PageSpeedResult from "./components/PageSpeedResult";
import Support from "./pages/Support/Support";
import PrivateRoute from "./components/PrivateRoute";
import Header from "./components/Header/Header";
import SeoStructuredData from "./components/SEO/SeoStructuredData";
import theme from "./theme";
import Profile from './pages/Profile';
import VerifyEmail from './pages/VerifyEmail';
import NotFound from './pages/NotFound';
import AuthInitializer from './components/AuthInitializer';
import MetaPixelTracker from './components/MetaPixelTracker';
import GoogleAnalyticsTracker from './components/GoogleAnalyticsTracker';
import GoogleAdsTracker from './components/GoogleAdsTracker';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import FacebookChat from './components/FacebookChat';

// Import new Dashboard pages
import DashboardBilling from './pages/Dashboard/Billing';
import DashboardPageSpeed from './pages/Dashboard/PageSpeed';
import DashboardSettings from './pages/Dashboard/Settings';
import DashboardTickets from './pages/Dashboard/Tickets';
import DashboardWebsites from './pages/Dashboard/Websites';
import WebsiteDetailPage from './pages/Dashboard/WebsiteDetailPage';
import TicketDetail from './pages/Dashboard/TicketDetail';
import NewTicket from './pages/Dashboard/NewTicket';
import AddWebsite from './pages/Dashboard/AddWebsite';
import AdminProducts from './pages/Dashboard/Admin/Products';
import AdminTickets from './pages/Dashboard/Admin/Tickets';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Layout component with header
const RootLayout = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <SeoStructuredData />
      {/* Add AuthInitializer here to check token on initial load */}
      <AuthInitializer />
      {/* Add Meta Pixel Tracker for analytics */}
      <MetaPixelTracker />
      {/* Add Google Analytics Tracker */}
      <GoogleAnalyticsTracker />
      {/* Add Google Ads Tracker for conversions and remarketing */}
      <GoogleAdsTracker />
      {/* Add Messenger Button */}
      <FacebookChat />
      <Header />
      <main className="flex-grow">
        <Outlet />
      </main>
      <div itemScope itemType="https://schema.org/Organization" style={{ display: 'none' }}>
        <span itemProp="name">Gravity Point Media</span>
        <span itemProp="description">Website Performance Analysis Tools</span>
        <img itemProp="logo" src="/logo512.png" alt="Gravity Point Media Logo" />
        <a itemProp="url" href={window.location.origin}>Gravity Point Media Website</a>
      </div>
    </div>
  );
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || (user.role !== 'admin' && !user.isAdmin)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  if (!user || (user.role !== 'admin' && !user.isAdmin)) {
    return null;
  }

  return children;
};

// Create router with routes configuration
const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <RootLayout />,
      children: [
        {
          index: true,
          element: <Support />
        },
        {
          path: "result/:hash",
          element: <PageSpeedResult />
        },
        {
          path: "login",
          element: <Login />
        },
        {
          path: "register",
          element: <Register />
        },
        {
          path: "forgot-password",
          element: <ForgotPassword />
        },
        {
          path: "reset-password/:token",
          element: <ResetPassword />
        },
        {
          path: "analyzer",
          element: <PageSpeed />
        },
        {
          path: "terms",
          element: <Terms />
        },
        {
          path: "privacy",
          element: <Privacy />
        },
        {
          path: "dashboard",
          element: (
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          ),
          children: [
            {
              path: "billing",
              element: <DashboardBilling />
            },
            {
              path: "pagespeed",
              element: <DashboardPageSpeed />
            },
            {
              path: "settings",
              element: <DashboardSettings />
            },
            {
              path: "products",
              element: <DashboardSettings activeTab="products" />
            },
            {
              path: "orders",
              element: <DashboardSettings activeTab="orders" />
            },
            {
              path: "tickets",
              element: <DashboardTickets />
            },
            {
              path: "tickets/new",
              element: <NewTicket />
            },
            {
              path: "tickets/:id",
              element: <TicketDetail />
            },
            {
              path: "websites",
              element: <DashboardWebsites />
            },
            {
              path: "websites/new",
              element: <AddWebsite />
            },
            {
              path: "websites/:id",
              element: <WebsiteDetailPage />
            },
            {
              path: "admin/products",
              element: (
                <AdminRoute>
                  <AdminProducts />
                </AdminRoute>
              )
            },
            {
              path: "admin/tickets",
              element: (
                <AdminRoute>
                  <AdminTickets />
                </AdminRoute>
              )
            }
          ]
        },
        {
          path: "home",
          element: <Home />
        },
        {
          path: "profile",
          element: (
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          )
        },
        {
          path: "verify-email",
          element: <VerifyEmail />
        },
        {
          path: "*",
          element: <NotFound />
        }
      ]
    }
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <HelmetProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </HelmetProvider>
    </ThemeProvider>
  );
}

export default App;
